import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import Badge from 'components/shared/Badge';
import Button from 'components/shared/Button';
import Foldout from 'components/shared/Foldout';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { simpleDate } from 'lib/date';
import {
  MatchReviews,
  OffenderMatch,
  OffenderOffense,
  OffenderSource,
  REVIEW_DECISION,
  ReviewDecision,
  SexOffenderCheckAction,
} from 'models/SexOffenderCheckAction';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';
import BackArrowIcon from 'svg/BackArrowIcon';
import LinkIcon from 'svg/LinkIcon';

import fallbackImageUrl from './SexOffenderCheckFallback.png';

// The one-line summary shown in the SexOffenderCheck row body. In a multi-action category the row
// itself is the clickable slideout trigger; this summary is plain text, no box, no chevron.
//
// A completed action whose screen result carries matches presents as `flagged` server-side (via
// SexOffenderCheck#effective_status). Three states, in order of adjudication progress:
//   - No confirmed matches yet: show the count-line ("N results were found…"). The imperative
//     "Review all results carefully" line drops off once the reviewer has recorded any decision.
//   - At least one candidate confirmed as a match: swap to the confirmation line so the row
//     signals the outcome instead of asking for review.
export function SexOffenderCheckSummary({ action }: { action: SexOffenderCheckAction }) {
  const count = action.matches.length;
  if (action.isMatch) {
    const confirmedCount = action.matchedMatches.length;
    if (confirmedCount > 0) {
      const noun = confirmedCount === 1 ? 'sex offender was' : 'sex offenders were';
      return (
        <SummaryText data-testid='sex-offender-check-flag'>
          {confirmedCount === 1 ? 'A' : confirmedCount} matched {noun} selected from {count} total{' '}
          {count === 1 ? 'result' : 'results'}.
        </SummaryText>
      );
    }
    return (
      <SummaryText data-testid='sex-offender-check-flag'>
        {count} {count === 1 ? 'result was' : 'results were'} found matching this patient’s demographics.
        {!action.hasAnyReview && ' Review all results carefully.'}
      </SummaryText>
    );
  }
  return (
    <SummaryText data-testid='sex-offender-check-result'>
      No matches were found in federal or state sex-offender registries.
    </SummaryText>
  );
}

// The SexOffenderCheck detail slideout. Rendered on `completed` — the integration always
// completes the action once it has a terminal result (matches or clear), mirroring DenialCheck's
// autonomous-completion model. Per-match Dismiss/Match decisions patch
// `action.outputs['sex_offender_match_reviews']` via saveWorkflowActionProgress — no lifecycle
// transition, no first-vs-subsequent-decision distinction. Empty state renders on `clear`.
export default function SexOffenderCheckReview({
  action,
  open,
  onClose,
  onSaveProgress,
  isSavingProgress,
}: {
  action: SexOffenderCheckAction;
  open: boolean;
  onClose: () => void;
  onSaveProgress: (actionId: string, outputs: WorkflowValuesInput) => void;
  isSavingProgress: boolean;
}) {
  const checkedName = action.checkedName;
  const matches = action.matches;
  const busy = isSavingProgress;

  // Every per-match decision (Dismiss OR Match, first or Nth) is a save_progress write —
  // patches `action.outputs['sex_offender_match_reviews']` on an already-completed action.
  //
  // The write MUST carry the current screen_result alongside the reviews: Rails' record_outputs
  // does `update!(outputs:)` whole-hog (workflow_action.rb:124), not a merge — omitting
  // screen_result would wipe it off action.outputs and the FE model (which reads from
  // action.outputs) would flip to the empty "No matches found" state on refresh.
  const applyDecision = (uuid: string, next: ReviewDecision | null) => {
    if (busy) return;
    const nextReviews = mergeReview(action.reviews, uuid, next);
    onSaveProgress(action.id, {
      sex_offender_screen_result: action.screenResult,
      sex_offender_match_reviews: nextReviews,
    });
  };

  return (
    <Foldout open={open} position='right' width='min(92%, 1000px)' onBackdropClick={onClose}>
      <Panel>
        <PanelHeader>
          <HeaderLeft>
            <BackButton type='button' onClick={onClose} aria-label='Back'>
              <BackArrowIcon width={20} height={20} />
            </BackButton>
            <HeaderText>
              <PanelTitle>Sex Offender Check</PanelTitle>
              <PanelSubtitle>
                Checking against federal and state-based sex offender registries to find any matches to this patient’s
                demographics
              </PanelSubtitle>
            </HeaderText>
          </HeaderLeft>
          <CloseButton type='button' onClick={onClose} aria-label='Close'>
            &times;
          </CloseButton>
        </PanelHeader>

        <PanelBody>
          {action.isMatch ? (
            <MatchList
              matches={matches}
              checkedName={checkedName}
              busy={busy}
              reviewFor={(uuid) => action.reviewFor(uuid)?.decision}
              applyDecision={applyDecision}
            />
          ) : (
            <EmptyState data-testid='sex-offender-check-no-matches'>
              <EmptyTitle>No Matches Found for ‘{checkedName}’</EmptyTitle>
              <EmptyBody>
                Olio did not identify this patient in any federal or state-based sex-offender registries.
              </EmptyBody>
            </EmptyState>
          )}
        </PanelBody>
      </Panel>
    </Foldout>
  );
}

// Return `reviews` with the entry for `uuid` set to `next` (or removed when next is null so
// the caller can undo a decision back to the un-adjudicated state). Never mutates the input.
// Preserves the order of untouched entries and appends new ones — stable for the FE render.
function mergeReview(reviews: MatchReviews, uuid: string, next: ReviewDecision | null): MatchReviews {
  const filtered = reviews.filter((r) => r.uuid !== uuid);
  if (next === null) return filtered;
  return [...filtered, { uuid, decision: next }];
}

// Client-side filter + sort controls layered over the loaded matches[]. Every dropdown is
// populated from the actual values present in the list (so the reviewer only picks from real
// options), with "All" as the neutral default. Dismissed cards keep their place in the sorted
// order — visually greyed by CardStyle — so the reviewer can still see what was decided.
type FilterKey = 'state' | 'sex' | 'race' | 'hairColor' | 'eyeColor';

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'state', label: 'State' },
  { key: 'sex', label: 'Sex' },
  { key: 'race', label: 'Race' },
  { key: 'hairColor', label: 'Hair Color' },
  { key: 'eyeColor', label: 'Eye Color' },
];

type SortKey = 'default' | 'age_desc' | 'age_asc' | 'name_asc' | 'name_desc';

function MatchList({
  matches,
  checkedName,
  busy,
  reviewFor,
  applyDecision,
}: {
  matches: OffenderMatch[];
  checkedName: string;
  busy: boolean;
  reviewFor: (uuid: string) => ReviewDecision | undefined;
  applyDecision: (uuid: string, next: ReviewDecision | null) => void;
}) {
  const [filters, setFilters] = useState<Record<FilterKey, string>>({
    state: '',
    sex: '',
    race: '',
    hairColor: '',
    eyeColor: '',
  });
  const [sort, setSort] = useState<SortKey>('default');
  // Dismissed cards are HIDDEN by default (matches the mockup's "View Dismissed Result" pill):
  // once a reviewer has dismissed a candidate, they've made a decision on it and it drops out of
  // the working list so their attention stays on cards still needing a decision. The toggle
  // below the filter row reveals them again — visually greyed via the card's `$dismissed` style.
  const [showDismissed, setShowDismissed] = useState(false);

  const options = useMemo(() => {
    const collect = (key: FilterKey) => {
      const values = new Set<string>();
      matches.forEach((m) => {
        const v = m[key];
        if (typeof v === 'string' && v.length > 0) values.add(v);
      });
      return Array.from(values).sort();
    };
    return {
      state: collect('state'),
      sex: collect('sex'),
      race: collect('race'),
      hairColor: collect('hairColor'),
      eyeColor: collect('eyeColor'),
    };
  }, [matches]);

  // Number of dismissed matches (drives the toggle affordance's visibility + label). Counted
  // pre-filter so hiding a dismissed one behind a Race/Hair filter doesn't make the "N dismissed"
  // count read as inconsistent with the toggle's effect.
  const dismissedCount = useMemo(
    () => matches.filter((m) => reviewFor(m.uuid) === REVIEW_DECISION.DISMISS).length,
    [matches, reviewFor]
  );

  const visible = useMemo(() => {
    const passesFilters = (m: OffenderMatch) =>
      FILTERS.every((f) => {
        const selected = filters[f.key];
        if (!selected) return true;
        return m[f.key] === selected;
      });
    const hidingDismissed = (m: OffenderMatch) => showDismissed || reviewFor(m.uuid) !== REVIEW_DECISION.DISMISS;
    const filtered = matches.filter((m) => hidingDismissed(m) && passesFilters(m));
    if (sort === 'default') return filtered;
    if (sort === 'age_desc' || sort === 'age_asc') {
      const numAge = (m: OffenderMatch) => {
        const n = Number(m.age);
        // Push unparseable ages to the end of whichever direction we're sorting.
        return Number.isFinite(n) ? n : sort === 'age_desc' ? -Infinity : Infinity;
      };
      return [...filtered].sort((a, b) => (sort === 'age_desc' ? numAge(b) - numAge(a) : numAge(a) - numAge(b)));
    }
    // Name sort — use the top-level `name` field when present, else assemble from first/last so
    // records that only carry the parts still sort sensibly. Empty names sort to the end in both
    // directions (localeCompare treats "" as smallest, so we swap the fallback per direction).
    const sortName = (m: OffenderMatch) => (m.name || `${m.firstName ?? ''} ${m.lastName ?? ''}`).trim().toLowerCase();
    return [...filtered].sort((a, b) => {
      const na = sortName(a);
      const nb = sortName(b);
      if (!na && !nb) return 0;
      if (!na) return 1;
      if (!nb) return -1;
      return sort === 'name_asc' ? na.localeCompare(nb) : nb.localeCompare(na);
    });
  }, [matches, filters, sort, showDismissed, reviewFor]);

  type OptionShape = { label: string; value: string };
  const sortOptions: { label: string; value: SortKey }[] = [
    { label: 'Default', value: 'default' },
    { label: 'Age (High to Low)', value: 'age_desc' },
    { label: 'Age (Low to High)', value: 'age_asc' },
    { label: 'Name (A–Z)', value: 'name_asc' },
    { label: 'Name (Z–A)', value: 'name_desc' },
  ];

  return (
    <>
      <ListHeaderRow>
        <MatchesHeader data-testid='sex-offender-matches-heading'>
          {matches.length} {matches.length === 1 ? 'match' : 'matches'} found for ‘{checkedName}’
        </MatchesHeader>
        <SortWrap>
          {/* Sort By renders as plain text with a subtle up/down icon prefix (Figma 9749:4778 —
              no pill chrome). The shared Select is still the trigger so we don't reinvent menu
              behavior; SORT_TEXT_STYLES strip the border, background, and default chevron so it
              reads as text with a menu on click. */}
          <ArrowUpDownIcon direction='none' width={10} height={10} />
          <Select<{ label: string; value: SortKey }>
            aria-label='Sort'
            options={sortOptions}
            value={sortOptions.find((o) => o.value === sort) ?? sortOptions[0]}
            onChange={(o) => setSort(o.value)}
            getOptionValue={(o) => o.value}
            getOptionLabel={(o) => o.label}
            formatOptionLabel={(o, meta) => (meta?.context === 'value' ? `Sort By: ${o.label}` : o.label)}
            isSearchable={false}
            styles={SORT_TEXT_STYLES}
          />
        </SortWrap>
      </ListHeaderRow>

      <FiltersRow>
        {FILTERS.map((f) => {
          const opts: OptionShape[] = [
            { label: 'All', value: '' },
            ...options[f.key].map((v) => ({ label: v, value: v })),
          ];
          return (
            <FilterCell key={f.key}>
              <Select<OptionShape>
                aria-label={f.label}
                options={opts}
                value={opts.find((o) => o.value === filters[f.key]) ?? opts[0]}
                onChange={(o) => setFilters((prev) => ({ ...prev, [f.key]: o.value }))}
                getOptionValue={(o) => o.value}
                getOptionLabel={(o) => o.label}
                formatOptionLabel={(o, meta) =>
                  meta?.context === 'value' ? twoTonePillLabel(f.label, o.label) : o.label
                }
                isSearchable={false}
                styles={FILTER_PILL_STYLES}
              />
            </FilterCell>
          );
        })}
      </FiltersRow>

      {dismissedCount > 0 && (
        <DismissedToggleRow>
          {/* Blue-outlined pill matching Figma 9749:4782 — border-selected primary blue,
              black75 text, 24px height. Uses the shared Button via a styled wrapper so we don't
              hand-roll another button element. */}
          <ViewDismissedButton
            type='button'
            variant='outline-primary'
            onClick={() => setShowDismissed((v) => !v)}
            data-testid='sex-offender-toggle-dismissed'>
            {showDismissed
              ? `Hide Dismissed (${dismissedCount})`
              : `View Dismissed Result${dismissedCount === 1 ? '' : 's'} (${dismissedCount})`}
          </ViewDismissedButton>
        </DismissedToggleRow>
      )}

      {visible.length === 0 ? (
        <NoResultsRow>No matches meet these filters.</NoResultsRow>
      ) : (
        <Cards>
          {visible.map((match) => {
            const decision = reviewFor(match.uuid);
            return (
              <MatchCard
                key={match.uuid}
                match={match}
                decision={decision}
                busy={busy}
                onDismiss={() =>
                  applyDecision(match.uuid, decision === REVIEW_DECISION.DISMISS ? null : REVIEW_DECISION.DISMISS)
                }
                onMatch={() =>
                  applyDecision(match.uuid, decision === REVIEW_DECISION.MATCH ? null : REVIEW_DECISION.MATCH)
                }
              />
            );
          })}
        </Cards>
      )}
    </>
  );
}

// A single sex-offender candidate. Renders the mockup layout: photo, header (name + badges +
// dismiss/match), a 6-col demographics grid, address, crimes (structured from
// `stateData.offenses[]` falling back to the singular `crime` string), and sourced registries
// (links from `sources[]`). Every field renders "–" when the API sent nothing — the mockup treats
// missing fields as visible gaps, not hidden.
function MatchCard({
  match,
  decision,
  busy,
  onDismiss,
  onMatch,
}: {
  match: OffenderMatch;
  decision: ReviewDecision | undefined;
  busy: boolean;
  onDismiss: () => void;
  onMatch: () => void;
}) {
  const isMatched = decision === REVIEW_DECISION.MATCH;
  const isDismissed = decision === REVIEW_DECISION.DISMISS;
  const displayName = fullName(match);
  const badges = matchBadges(match);
  const crimes = matchCrimes(match);
  const sources = matchSources(match);

  return (
    <Card data-testid='sex-offender-match-card' $matched={isMatched} $dismissed={isDismissed}>
      <Photo match={match} />
      <CardBody>
        <CardHeader>
          <NameAndBadges>
            <MatchName>{displayName}</MatchName>
            {badges.map((badge, i) => (
              <Badge key={`${badge}-${i}`} color='red'>
                {badge}
              </Badge>
            ))}
          </NameAndBadges>
          <Actions>
            {/* Dismiss and Match are toggles — clicking a set state clears it. Both disabled while
                a mutation is in flight (busy). Both stay neutral outline regardless of state — the
                card's blue border (isMatched) and greyed opacity (isDismissed) carry the visual
                signal, and the label swap reads out the state in copy. AdjudicationButton tightens
                the shared Button's default padding + weight to match the mockup pill shape (same
                trick FiltersButton in InboundReferralsListPage uses for its compact pill). */}
            <AdjudicationButton type='button' variant='outline-gray' onClick={onDismiss} disabled={busy || isMatched}>
              <IconGlyph aria-hidden>✕</IconGlyph>
              {isDismissed ? 'Dismissed' : 'Dismiss'}
            </AdjudicationButton>
            <AdjudicationButton type='button' variant='outline-gray' onClick={onMatch} disabled={busy || isDismissed}>
              <IconGlyph aria-hidden>✓</IconGlyph>
              {isMatched ? 'Remove Match' : 'Match'}
            </AdjudicationButton>
          </Actions>
        </CardHeader>

        <DemographicsGrid>
          <Field label='Sex' value={match.sex} />
          <Field label='DOB' value={simpleDate(match.dob) || '–'} />
          <Field label='Age' value={match.age} />
          <Field label='Race' value={match.race} />
          <Field label='Hair Color' value={match.hairColor} />
          <Field label='Eye Color' value={match.eyeColor} />
        </DemographicsGrid>

        <Field label='Address' value={match.address} block />

        <CrimesSection>
          <FieldLabel>Crimes</FieldLabel>
          {crimes.length === 0 ? (
            <FieldValue>No offense details available</FieldValue>
          ) : (
            <CrimesList>
              {crimes.map((line, i) => (
                <li key={`${line.offense}-${i}`}>
                  <CrimeOffense>{line.offense}</CrimeOffense>
                  {line.meta && <CrimeMeta>{line.meta}</CrimeMeta>}
                </li>
              ))}
            </CrimesList>
          )}
        </CrimesSection>

        {sources.length > 0 && (
          <SourcesSection>
            <FieldLabel>Sourced Registries:</FieldLabel>
            <SourcesList>
              {sources.map((s, i) => (
                <SourceItem key={`${s.name}-${i}`}>
                  <SourceLink href={s.url ?? '#'} target='_blank' rel='noreferrer noopener'>
                    {s.name}
                    <LinkIcon width={10} height={10} color={colors.blue100} />
                  </SourceLink>
                </SourceItem>
              ))}
            </SourcesList>
          </SourcesSection>
        )}
      </CardBody>
    </Card>
  );
}

// The photo cell. `offenderImageUrlR2` (Olio-hosted mirror) is preferred over `offenderImageUrl`
// (state registry direct URL, unreliable / CORS-hostile). Missing URL OR a URL that fails to
// resolve (404, CORS, network) both fall back to a bundled placeholder image.
function Photo({ match }: { match: OffenderMatch }) {
  const src = match.offenderImageUrlR2 || match.offenderImageUrl || fallbackImageUrl;
  const [failed, setFailed] = useState(false);
  const displayed = failed ? fallbackImageUrl : src;
  return (
    <PhotoCell>
      <PhotoImg src={displayed} alt='' loading='lazy' onError={() => setFailed(true)} />
    </PhotoCell>
  );
}

// A one-line field label + value block, used across the demographics grid and the address row.
// A missing value renders as "–" so gaps stay visible (matches the mockup).
function Field({ label, value, block }: { label: string; value?: string | null; block?: boolean }) {
  return (
    <FieldBlock $block={block}>
      <FieldLabel>{label}</FieldLabel>
      <FieldValue>{value || '–'}</FieldValue>
    </FieldBlock>
  );
}

function fullName(match: OffenderMatch): string {
  if (match.name) return match.name;
  return [match.firstName, match.middleName, match.lastName].filter(Boolean).join(' ') || '–';
}

// The badges strip next to the name. Preserves the mockup's order — designation (e.g. "Level 1
// Predator"), then compliance status ("Active"), then risk level / tier ("Tier III"). Empty
// values are dropped rather than rendered as blank pills. De-duped case-insensitively because
// some registries populate `stateData.designation` and top-level `riskLevel` with the same
// string (e.g. "Level 1 Predator") — no reason to render the same pill twice.
function matchBadges(match: OffenderMatch): string[] {
  const state = match.stateData ?? {};
  const seen = new Set<string>();
  const out: string[] = [];
  for (const candidate of [state.designation, state.status, match.riskLevel]) {
    if (typeof candidate !== 'string' || candidate.length === 0) continue;
    const key = candidate.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(candidate);
  }
  return out;
}

interface CrimeLine {
  offense: string;
  meta?: string;
}

// Per-crime lines the FE renders as a bullet list. Real offenders.io data is far messier than
// the schema suggests, so this is deliberately defensive:
//   - Prefer stateData.offenses[] (extensive mode) and pull out `offense` + a formatted meta line
//     ("Feb 8, 2012 · Washington"). Drop the meta line entirely when both fields are missing.
//   - `offense` may itself contain embedded `\n` (VA registry aggregator squashes multiple
//     statutes + `Date Convicted:` blocks into a single string). The render uses
//     `white-space: pre-line`, so we pass the raw text through — parsing it would be fragile.
//   - When stateData.offenses is absent or empty, fall back to the top-level `crime` string and
//     split it on `\n` so multi-crime `crime` blobs (Booker Smith etc.) render as separate bullets
//     instead of one wall.
//   - Skip entries whose offense text ended up empty after trim (offenses: [] with placeholder
//     objects, or a bare `\n` from the crime fallback).
function matchCrimes(match: OffenderMatch): CrimeLine[] {
  const offenses: OffenderOffense[] | undefined = match.stateData?.offenses;
  if (Array.isArray(offenses) && offenses.length > 0) {
    return offenses
      .map((o) => {
        const offense = (o.offense ?? '').trim();
        const date = simpleDate(o.convictionDate ?? undefined) || (o.convictionDate ?? '').trim();
        const meta = [date, (o.jurisdiction ?? '').trim()].filter(Boolean).join(' · ') || undefined;
        return { offense, meta };
      })
      .filter((line) => line.offense.length > 0);
  }
  return (match.crime ?? '')
    .split('\n')
    .map((line) => ({ offense: line.trim() }))
    .filter((line) => line.offense.length > 0);
}

// The named external-link entries next to `Sourced Registries:`. offenders.io returns each source
// as `{id, name, url}`; we drop entries without a URL (can't link) and de-null the display name.
function matchSources(match: OffenderMatch): { name: string; url: string | null }[] {
  const sources: OffenderSource[] | undefined = match.sources;
  if (!Array.isArray(sources)) return [];
  return sources.filter((s) => !!s.url).map((s) => ({ name: s.name ?? 'Source', url: s.url ?? null }));
}

const SummaryText = styled(BodySmall)`
  color: ${colors.black75};
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${colors.white};
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 24px 32px 20px;
  border-bottom: 1px solid ${colors.black10};
  gap: 16px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex: 1;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${colors.black75};
  display: inline-flex;
  align-items: center;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${colors.black100};
`;

const PanelSubtitle = styled(BodySmall)`
  color: ${colors.black75};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: ${colors.black75};
  padding: 0 4px;
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MatchesHeader = styled(BodySmallBold)`
  color: ${colors.black100};
  font-size: 16px;
`;

const ListHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const SortWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${colors.black100};
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
`;

const DismissedToggleRow = styled.div`
  display: flex;
  justify-content: center;
`;

const FilterCell = styled.div`
  display: flex;
  width: 100%;
`;

const NoResultsRow = styled.div`
  padding: 24px;
  font-size: 13px;
  color: ${colors.black50};
  text-align: center;
  border: 1px dashed ${colors.black10};
  border-radius: 4px;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Card = styled.div<{ $matched: boolean; $dismissed: boolean }>`
  display: grid;
  grid-template-columns: 148px 1fr;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${colors.black10};
  border-radius: 6px;
  background: ${colors.white};
  ${({ $matched }) =>
    $matched &&
    css`
      border-color: ${colors.blue100};
      background: ${colors.blue5};
    `}
  ${({ $dismissed }) =>
    $dismissed &&
    css`
      opacity: 0.55;
    `}
`;

const PhotoCell = styled.div`
  width: 148px;
  height: 148px;
`;

const PhotoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

// Actions (Dismiss/Match) must always sit in the top-right corner. Dropping flex-wrap on the
// header prevents the whole Actions block from wrapping below when NameAndBadges grows wide with
// long red risk-level badges ("Sexually Violent Predator" etc.). NameAndBadges takes the
// remaining space (flex: 1) and wraps its OWN children instead — so a long badge row just adds
// vertical height on the left, while Match/Dismiss stay pinned right.
const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const NameAndBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
`;

const MatchName = styled(BodySmallBold)`
  font-size: 15px;
  color: ${colors.black100};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

// Tightens the shared Button for the per-match adjudication row. Figma spec (9749:4716):
// 24px height, 12px Lato Regular, 4px radius, rgba(2,19,32,0.1) border (lighter than the
// filter pill's border), rgba(2,19,32,0.75) text. Same styled(Button) pattern FiltersButton
// uses in InboundReferralsListPage to override the shared Button's chunkier defaults.
const AdjudicationButton = styled(Button)`
  height: 24px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: normal;
  line-height: 14px;
  letter-spacing: 0.18px;
  gap: 6px;
  border-radius: 4px;
  border-color: ${colors.black10};
  color: ${colors.black75};
`;

const IconGlyph = styled.span`
  display: inline-flex;
  font-size: 12px;
  line-height: 1;
`;

// Two-tone pill label ("State:" at 50% opacity, "All" at 100%) — matches the Figma spec
// (9749:4646) for the collapsed filter pill's inner text. Kept inline instead of a shared
// component because it's a single-file rendering choice for the filter/sort formatOptionLabel.
function twoTonePillLabel(prefix: string, value: string) {
  return (
    <>
      <PillPrefix>{prefix}: </PillPrefix>
      <PillValue>{value}</PillValue>
    </>
  );
}

const PillPrefix = styled.span`
  color: ${colors.black50};
`;

const PillValue = styled.span`
  color: ${colors.black100};
`;

// react-select style overrides that stamp the shared Select onto the Figma filter-pill spec
// (9749:4645): 32px height, 12px Lato Regular, 4px radius, black25 border, 12/16 horizontal
// padding, black75 caret. Merged onto react-select's built-in defaults per style key (using
// `...provided`) so anything we don't touch keeps its stock behavior.
const FILTER_PILL_STYLES = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    height: 32,
    width: '100%',
  }),
  control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => ({
    ...provided,
    minHeight: 32,
    height: 32,
    width: '100%',
    fontSize: 12,
    lineHeight: '12px',
    borderRadius: 4,
    borderColor: state.isFocused ? colors.blue100 : colors.black25,
    boxShadow: 'none',
    cursor: 'pointer',
    '&:hover': { borderColor: colors.blue100 },
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    height: 32,
    padding: '0 4px 0 12px',
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: 12,
    letterSpacing: '0.18px',
    margin: 0,
  }),
  input: (provided: Record<string, unknown>) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  indicatorsContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    height: 32,
  }),
  dropdownIndicator: (provided: Record<string, unknown>) => ({
    ...provided,
    padding: '0 12px 0 4px',
    color: colors.black75,
    '& svg': { width: 12, height: 12 },
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: 12,
    borderRadius: 4,
    zIndex: 100,
  }),
  option: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: 12,
    padding: '8px 12px',
  }),
};

// Sort By reads as plain text on the mockup (Figma 9749:4778) — an up/down icon + one line of
// text, no border, no background, no chevron. These overrides strip react-select down to that
// visual while keeping its click-to-open menu behavior.
const SORT_TEXT_STYLES = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    height: 20,
    minWidth: 180,
  }),
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    minHeight: 20,
    height: 20,
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
    cursor: 'pointer',
    '&:hover': { border: 'none' },
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    height: 20,
    padding: 0,
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: 12,
    color: colors.black100,
    letterSpacing: '0.18px',
    margin: 0,
  }),
  input: (provided: Record<string, unknown>) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: () => ({ display: 'none' }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: 12,
    borderRadius: 4,
    zIndex: 100,
    minWidth: 200,
    right: 0,
  }),
  option: (provided: Record<string, unknown>) => ({
    ...provided,
    fontSize: 12,
    padding: '8px 12px',
  }),
};

// The "View Dismissed Result" toggle (Figma 9749:4782). Primary-blue border, black75 text,
// 24px height — different color balance from AdjudicationButton (which uses the lighter
// border-lighter variable). Kept as a styled(Button) so we inherit all the shared Button's
// state handling.
const ViewDismissedButton = styled(Button)`
  height: 24px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: normal;
  line-height: 14px;
  letter-spacing: 0.18px;
  border-radius: 4px;
  border-color: ${colors.blue100};
  color: ${colors.black75};
  background: ${colors.white};
`;

const DemographicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px 16px;
`;

const FieldBlock = styled.div<{ $block?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${({ $block }) =>
    $block &&
    css`
      grid-column: 1 / -1;
    `}
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: ${colors.black50};
`;

const FieldValue = styled.div`
  font-size: 13px;
  color: ${colors.black100};
`;

const CrimesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CrimesList = styled.ul`
  margin: 0;
  padding-left: 18px;
  color: ${colors.black100};
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// pre-line preserves embedded newlines from unnormalized registry data (VA aggregator dumps
// multiple statutes + Date Convicted/Court/Case Number rows into a single string). Without this,
// those `\n`s collapse and the offense reads as one long horizontal wall.
const CrimeOffense = styled.div`
  white-space: pre-line;
`;

const CrimeMeta = styled.div`
  font-size: 11px;
  color: ${colors.black50};
  margin-top: 2px;
`;

const SourcesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SourcesList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SourceItem = styled.li`
  font-size: 13px;
  color: ${colors.blue100};
`;

const SourceLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${colors.blue100};
  text-decoration: underline;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 64px 32px;
  text-align: center;
`;

const EmptyTitle = styled(BodySmallBold)`
  font-size: 16px;
  color: ${colors.black100};
`;

const EmptyBody = styled(BodySmall)`
  color: ${colors.black75};
  max-width: 420px;
`;
